import { HireCarPassengers, IOptionsWithTypes } from '@eventr/common'
import { ColoursObj, Icon } from '@eventr/common-storybook'
import { Stack } from '@mui/material'
import { useMemo } from 'react'
import { cardTranslation } from '../../../i18n/translateHelpers'
import { useFormatValue } from '../../hooks/useFormatValue'
import { useScreenTypeContext } from '../../state/screenTypeProvider'
import { useMatchTagsAndUsers } from '../hooks/useMatchTagsAndUsers'
import { useObjectContext } from '../providers/objectProvider'
import { CardText } from '../subComponents/cardText'
import { ChatButton } from '../subComponents/chatButton'
import { EditButton } from '../subComponents/editButton'
import { ExpandableSection } from '../subComponents/expendableSection'
import { RowInfo } from '../subComponents/rowInfo'
import { RowInfoNotes } from '../subComponents/rowInfoNotes'
import { HireCarAttachments } from './hireCarAttachment'
import { HireCarDriver } from './hireCarDriver'
import { HireCarPassenger } from './hireCarPassenger'

interface IHireCarInfoProps {
    pick_up: string
    drop_off: string
    booking_ref?: string
    pick_up_location?: string
    drop_off_location?: string
    documents?: string
    additional_info?: string
    passengers?: HireCarPassengers[]
    vehicle_type?: string
    provider?: string
    chatNames: string[]
}

export const HireCarInfo = ({
    booking_ref,
    pick_up,
    pick_up_location,
    drop_off,
    drop_off_location,
    documents,
    additional_info,
    passengers,
    vehicle_type,
    provider,
    chatNames,
}: IHireCarInfoProps) => {
    const { screenType } = useScreenTypeContext()
    const { _id, sectionID } = useObjectContext()
    const { past } = useObjectContext()
    const { formatValue } = useFormatValue()
    const { matchTagsAndUser } = useMatchTagsAndUsers()

    const allGuests = useMemo(() => {
        const all: IOptionsWithTypes[] = []
        passengers?.forEach((passenger) => {
            all.push(...(passenger.users ?? []))
        })
        return all
    }, [passengers])

    const end = useMemo(() => {
        if (screenType === 'edit')
            return <EditButton path={`/edit/Section/${sectionID}/${_id}`} />

        return <ChatButton _id={_id} users={allGuests} names={chatNames} />
    }, [screenType, allGuests, _id, chatNames, sectionID])

    const driverList = useMemo(() => {
        if ((passengers?.length ?? 0) === 0) {
            return null
        }
        return (passengers ?? [])
            .filter((passenger) => passenger.isDriver)
            .map((driver) => driver.users)
    }, [passengers])

    const passengerList = useMemo(() => {
        if ((passengers?.length ?? 0) === 0) {
            return null
        }
        return (passengers ?? [])
            .filter((passenger) => !passenger.isDriver)
            .map((passenger) => passenger.users)
    }, [passengers])

    const passenger = useMemo(() => {
        if ((passengers?.length ?? 0) === 0) {
            return null
        }
        return (passengers ?? []).filter((passenger) => {
            if (
                passenger?.users?.find((user) => {
                    return matchTagsAndUser(user)
                })
            ) {
                return true
            }

            return false
        })
    }, [passengers, matchTagsAndUser])

    return (
        <Stack direction={'column'} gap={'8px'}>
            <ExpandableSection
                mid={
                    booking_ref ? (
                        <CardText variant="body1">{booking_ref}</CardText>
                    ) : (
                        <CardText variant="body1">
                            {cardTranslation('Additional Information')}
                        </CardText>
                    )
                }
                end={end}
            >
                <Stack spacing={'8px'}>
                    <CardText variant="bodyBold">
                        {' '}
                        {cardTranslation('Pick-up & Drop-off')}{' '}
                    </CardText>
                    {pick_up && (
                        <Stack flexDirection={'column'}>
                            <RowInfo
                                start={
                                    <Icon
                                        name={'Right'}
                                        bubble
                                        bubbleColour={
                                            past
                                                ? ColoursObj.shades.shade2
                                                : ColoursObj.core.secondary
                                        }
                                    />
                                }
                                middle={
                                    <CardText>
                                        {cardTranslation('Pick-up')}
                                    </CardText>
                                }
                                end={
                                    <CardText>
                                        {formatValue(pick_up, 'datetime')}
                                    </CardText>
                                }
                            />
                            {pick_up_location && (
                                <CardText variant="bodySubtext">
                                    {pick_up_location}
                                </CardText>
                            )}
                        </Stack>
                    )}
                    {drop_off && (
                        <Stack flexDirection={'column'}>
                            <RowInfo
                                start={
                                    <Icon
                                        name={'Left'}
                                        bubble
                                        bubbleColour={
                                            past
                                                ? ColoursObj.shades.shade2
                                                : ColoursObj.core.secondary
                                        }
                                    />
                                }
                                middle={
                                    <CardText>
                                        {cardTranslation('Drop-off')}
                                    </CardText>
                                }
                                end={
                                    <CardText>
                                        {formatValue(drop_off, 'datetime')}
                                    </CardText>
                                }
                            />
                            {drop_off_location && (
                                <CardText variant="bodySubtext">
                                    {drop_off_location}
                                </CardText>
                            )}
                        </Stack>
                    )}
                    {(vehicle_type || provider) && (
                        <CardText variant="bodyBold">
                            {' '}
                            {cardTranslation('Car Information')}{' '}
                        </CardText>
                    )}

                    {vehicle_type && (
                        <RowInfo
                            start={
                                <Icon
                                    name={'Car_Type'}
                                    bubble
                                    bubbleColour={
                                        past
                                            ? ColoursObj.shades.shade2
                                            : ColoursObj.coreBrights.secondary
                                    }
                                />
                            }
                            middle={
                                <CardText variant="body1">
                                    {' '}
                                    {cardTranslation('Vehicle Type')}{' '}
                                </CardText>
                            }
                            end={
                                <CardText variant="body1">
                                    {vehicle_type}
                                </CardText>
                            }
                        />
                    )}

                    {provider && (
                        <RowInfo
                            start={
                                <Icon
                                    name={'Group'}
                                    bubble
                                    bubbleColour={
                                        past
                                            ? ColoursObj.shades.shade2
                                            : ColoursObj.coreBrights.primary
                                    }
                                />
                            }
                            middle={
                                <CardText variant="body1">
                                    {' '}
                                    {cardTranslation('Provider')}{' '}
                                </CardText>
                            }
                            end={
                                <CardText variant="body1">{provider}</CardText>
                            }
                        />
                    )}
                    {(documents || additional_info) && (
                        <CardText variant="bodyBold">
                            {' '}
                            {cardTranslation('Attachments')}{' '}
                        </CardText>
                    )}
                    {additional_info && (
                        <RowInfoNotes notes={additional_info} />
                    )}
                    {documents && <HireCarAttachments extra_docs={documents} />}
                    {(screenType === 'personal' || screenType === 'upNext') &&
                        passenger &&
                        passenger.some((p) => p.notes || p.documents) && (
                            <CardText variant="bodyBold">
                                {cardTranslation('Personal Information')}
                            </CardText>
                        )}
                    {(screenType === 'personal' || screenType === 'upNext') &&
                        passenger &&
                        passenger.map(
                            (p, index) =>
                                p.notes && (
                                    <RowInfoNotes key={index} notes={p.notes} />
                                )
                        )}
                    {(screenType === 'personal' || screenType === 'upNext') &&
                        passenger &&
                        passenger.map(
                            (p, index) =>
                                p.documents && (
                                    <HireCarAttachments
                                        key={index}
                                        extra_docs={p.documents}
                                    />
                                )
                        )}
                    {driverList && driverList.length > 0 && (
                        <HireCarDriver driver={driverList.flat()} />
                    )}
                    {passengerList && (
                        <HireCarPassenger passenger={passengerList.flat()} />
                    )}
                </Stack>
            </ExpandableSection>
        </Stack>
    )
}
